// Slip Share table — column definitions and the shared validation states.
// page renders until the backend exists.
//
// A "slip share" is one underwriter's percentage of a single binder (LimeIQ
// key / UMR), so several rows share a key and only make sense read together:
// the percentages for a key are complete when they add up to exactly 100.

export function getSlipShareTableColumns() {
  return [
    { key: 'limeiq_key', label: 'LimeIQ Key', sortable: true, searchable: true },
    { key: 'umr', label: 'UMR', sortable: true, searchable: true },
    { key: 'risk_code', label: 'Risk Code', sortable: true, searchable: true },
    { key: 'underwriter', label: 'Underwriter', sortable: true, searchable: true },
    { key: 'lead', label: 'Lead?', sortable: true, searchable: true },
    { key: 'syndicate_no', label: 'Syndicate No.', sortable: true, searchable: true },
    { key: 'slip_share_pct', label: 'Slip Share %', sortable: true, searchable: true },
    { key: 'lloyds', label: "Lloyd's", sortable: true, searchable: true },
    { key: 'fcp', label: 'FCP', sortable: true, searchable: true },
    { key: 'broker', label: 'Broker', sortable: true, searchable: true },
    { key: 'coverholder', label: 'Coverholder', sortable: true, searchable: true },
    { key: 'tpa', label: 'TPA', sortable: true, searchable: true },
    { key: 'billing_type', label: 'Billing Type', sortable: true, searchable: true },
    { key: 'complete', label: 'Slip Share Complete', sortable: true, searchable: true }
  ]
}

// A key's shares are complete only at exactly 100. Under and over are both
// wrong but mean different things to whoever has to fix them, so they're
// distinguished rather than lumped into one "invalid".
export const SHARE_STATE = {
  COMPLETE: 'complete',
  UNDER: 'under',
  OVER: 'over',
  // Adds up to 100, but an underwriter on the key has no share at all (added
  // as a follower with nothing populated, or 0%). Still incomplete - every
  // underwriter on a key needs a share of its own - and billed to the lead.
  MISSING: 'missing',
  // Nobody has supplied anything for this key yet. Deliberately not the
  // same as UNDER: one is an outstanding task for whoever owns the data,
  // the other is a mistake in what they already gave us.
  NO_DATA: 'nodata',
  // Hiscox-led keys bill 100% to Hiscox regardless of the recorded split, so
  // the 100% rule does not apply to them. Shown apart from red/green rather
  // than as a pass, because nothing was validated - the rule was waived.
  EXEMPT: 'exempt'
}

// Totals arrive scaled to millionths, matching the six decimal places the share
// itself is stored at. A coarser scale silently rounds a near-miss up to a whole
// 100, so a key the server had already flagged as incomplete showed green.
// 100000000 is far inside Number.MAX_SAFE_INTEGER, so the integer comparison
// below is exact.
export const COMPLETE_UNITS = 100000000 // 100.000000%

export function shareStateFor(total, exempt = false, missingCount = 0) {
  // Checked before the totals, not after: an exempt key is not "complete at
  // 100" or "wrong below it" - the question is not asked of it at all.
  if (exempt) return SHARE_STATE.EXEMPT
  // null arrives from the server for a key no participant has a share on.
  // Treating it as 0 would report it as UNDER, i.e. as bad data rather than
  // absent data.
  if (total === null || total === undefined) return SHARE_STATE.NO_DATA
  if (total === COMPLETE_UNITS) {
    return missingCount > 0 ? SHARE_STATE.MISSING : SHARE_STATE.COMPLETE
  }
  return total < COMPLETE_UNITS ? SHARE_STATE.UNDER : SHARE_STATE.OVER
}
